// 9 Oct: the person icon a known address sees in the lobby and the game header, and the sheet it opens: proof of identity
// for a returning player. Shown only while the server says the address has played before and is not yet proved (GET
// /me's returning.known without verified), so a stranger typing an address learns only that it has played; a tap opens a
// small sheet with one way forward, sign-in with the joined address filled in, which comes back into the battle
// (lib/proof). The icon is our own glyph, strokes on the current colour, no badge. The sheet is drawn through a portal
// (the game's screens animate with transforms, which would pin a fixed sheet to them) and is a dialog: Escape, the
// backdrop and Not now close it, Tab stays inside, and the focus comes back to the icon. It closes by itself once the
// address is proved. Nothing here logs.
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import type { MeResponse } from '@shared/types';
import { useGame } from '@/store/game';
import { offersProof, signInForProof, type ProofWhere } from '@/lib/proof';
import type { Session } from '@/lib/session';
import { buttonClass } from './Button';

/** The sheet's words and the message once the address is proved (the game shows it as a toast). Registrar voice: short and specific. */
export const PROOF = {
  icon: 'Played before? Sign in',
  title: 'Played before?',
  body: 'Sign in with the address you joined with to bring your record and your results with you.',
  signIn: 'Sign in',
  notNow: 'Not now',
  done: 'Signed in. Your record and your results come with you.',
} as const;

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
const QUIET = 'inline-flex min-h-11 items-center px-1 text-sm font-semibold text-iv-strong underline-offset-4 hover:underline';

/** A person, drawn as strokes on a 24-unit grid: a head and shoulders, nothing filled. */
export function PersonGlyph({ size = 22, className = '' }: { size?: number; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" className={className}>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M4.5 20.5c.6-4.2 3.6-6.5 7.5-6.5s6.9 2.3 7.5 6.5" />
    </svg>
  );
}

/**
 * In the game the icon reads the game store; on the result page, which keeps its own /me and session (the page's own, or
 * the device's for a result link), and on the details step (9 Oct), which keeps its own /me, the page hands them in and
 * the sheet comes back to that page.
 */
export function ProofMark({ me: given, session: givenSession, where = 'play', className = '' }: { me?: MeResponse; session?: Session; where?: ProofWhere; className?: string }) {
  const storeMe = useGame((s) => s.me); const storeSession = useGame((s) => s.session);
  const me = where === 'play' ? storeMe : given; const session = where === 'play' ? storeSession : givenSession;
  const [open, setOpen] = useState(false);
  const titleId = useId(); const bodyId = useId();
  const icon = useRef<HTMLButtonElement>(null); const box = useRef<HTMLDivElement>(null);
  const show = session !== undefined && offersProof(me);

  // Proved meanwhile (the game verified the address): the sheet goes with the icon.
  useEffect(() => { if (!show) setOpen(false); }, [show]);

  useEffect(() => {
    if (!open) return;
    box.current?.querySelector<HTMLElement>('a[href]')?.focus();
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    const before = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = before;
      icon.current?.focus?.();
    };
  }, [open]);

  // Tab stays inside the sheet while it is open.
  const trap = (e: KeyboardEvent) => {
    if (e.key !== 'Tab' || !box.current) return;
    const items = [...box.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
    if (!items.length) return;
    const first = items[0]; const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  if (!show || !session) return null;
  const close = () => setOpen(false);
  return (
    <>
      <button ref={icon} type="button" onClick={() => setOpen(true)} aria-label={PROOF.icon} aria-haspopup="dialog" aria-expanded={open}
        className={`inline-flex size-11 shrink-0 items-center justify-center rounded-full text-current transition-transform active:translate-y-[1px] focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-iv ${className}`}>
        <PersonGlyph />
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:px-4">
          {/* For the pointer only: Escape and Not now are the keyboard's ways out. */}
          <div data-part="proof-backdrop" className="absolute inset-0 bg-ink/45" onClick={close} />
          <div ref={box} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={bodyId} onKeyDown={trap}
            className="relative flex w-full max-w-md flex-col gap-4 rounded-t-card bg-surface px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-5 text-ink sm:rounded-card">
            <span aria-hidden="true" className="text-brand-strong"><PersonGlyph size={30} /></span>
            <h2 id={titleId} className="display text-[1.75rem] leading-tight text-ink">{PROOF.title}</h2>
            <p id={bodyId} className="leading-snug text-ink-2">{PROOF.body}</p>
            <Link to={signInForProof(session, where)} className={buttonClass({ variant: 'primary', size: 'md' })}>{PROOF.signIn}</Link>
            <button type="button" onClick={close} className={`${QUIET} self-center`}>{PROOF.notNow}</button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
